import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  chart as rawChart,
  getColorDomain,
  getDefaultOptionsValues,
  getOptionsConfig,
  getTypeName,
} from '@rawgraphs/rawgraphs-core'
import get from 'lodash/get'
import {
  buildBrandColorScaleOption,
  buildBrandPalette,
  buildPaletteColorScaleOption,
} from '../services/brandTheme'
import { normalizeChartMapping } from '../services/normalizeChartMapping'
import { CHART_IDS } from '../charts/chartIds'

const SOURCE_DRIVEN_NODE_COLOR_CHART_IDS = new Set([
  CHART_IDS.ALLUVIAL_DIAGRAM,
  CHART_IDS.ARC_DIAGRAM,
  CHART_IDS.SANKEY_DIAGRAM,
])

function uniqueDefined(values) {
  return Array.from(
    new Set(values.filter((value) => value !== undefined && value !== null && value !== ''))
  )
}

function mergeExistingOpacities(existingScale, nextScale) {
  const existingByDomain = new Map(
    (existingScale?.userScaleValues || []).map((item) => [
      String(item?.domain),
      item?.opacity,
    ])
  )

  return {
    ...nextScale,
    defaultOpacity: Number.isFinite(Number(existingScale?.defaultOpacity))
      ? Number(existingScale.defaultOpacity)
      : nextScale.defaultOpacity ?? 1,
    userScaleValues: (nextScale?.userScaleValues || []).map((item) => {
      const existingOpacity = existingByDomain.get(String(item?.domain))
      return {
        ...item,
        ...(existingOpacity !== undefined ? { opacity: existingOpacity } : {}),
      }
    }),
  }
}

export default function useChartColorSync({
  currentChart,
  data,
  mapping,
  visualOptions,
  setVisualOptions,
  brandTheme,
}) {
  const [chartColorSyncMode, setChartColorSyncMode] = useState('defaults')
  const [lastAppliedBrandPaletteSignature, setLastAppliedBrandPaletteSignature] =
    useState(null)

  const currentBrandPaletteSignature = useMemo(
    () => buildBrandPalette(brandTheme).join('|'),
    [brandTheme]
  )

  const chartPaletteDirty = useMemo(
    () =>
      chartColorSyncMode === 'brand' &&
      !!lastAppliedBrandPaletteSignature &&
      lastAppliedBrandPaletteSignature !== currentBrandPaletteSignature,
    [
      chartColorSyncMode,
      currentBrandPaletteSignature,
      lastAppliedBrandPaletteSignature,
    ]
  )

  const getCurrentChartColorDomain = useCallback(() => {
    if (!currentChart?.visualOptions || !data?.dataset) {
      return []
    }

    const optionsConfig = getOptionsConfig(currentChart.visualOptions)
    if (!optionsConfig.colorScale) {
      return []
    }

    const chartId = currentChart?.metadata?.id
    const colorDimension = optionsConfig.colorScale.dimension

    if (chartId === CHART_IDS.ALLUVIAL_DIAGRAM) {
      const steps = get(mapping, 'steps.value', [])
      return steps.flatMap((step) =>
        uniqueDefined(
          data.dataset.map((row) => {
            const stepValue = row?.[step]
            return stepValue === undefined || stepValue === null || stepValue === ''
              ? null
              : `${step} - ${stepValue}`
          })
        )
      )
    }

    if (SOURCE_DRIVEN_NODE_COLOR_CHART_IDS.has(chartId)) {
      const sourceColumn = get(mapping, '[source].value[0]')
      const targetColumn = get(mapping, '[target].value[0]')
      const domainValues = data.dataset.flatMap((row) => [
        sourceColumn ? row?.[sourceColumn] : undefined,
        targetColumn ? row?.[targetColumn] : undefined,
      ])
      const uniqueDomain = uniqueDefined(domainValues)

      if (uniqueDomain.length > 0) {
        return uniqueDomain
      }
    }

    const colorMappedColumn = get(mapping, `[${colorDimension}].value[0]`)
    const colorDataType = colorMappedColumn
      ? getTypeName(data?.dataTypes?.[colorMappedColumn])
      : null

    if (colorMappedColumn && colorDataType) {
      const colorDataset = data.dataset
        .map((row) => row[colorMappedColumn])
        .filter((value) => value !== undefined)

      return getColorDomain(colorDataset, colorDataType, 'ordinal')
    }

    try {
      const viz = rawChart(currentChart, {
        data: data.dataset,
        mapping: normalizeChartMapping(currentChart, mapping, data.dataTypes),
        dataTypes: data.dataTypes,
      })
      const vizData = viz?._getVizData?.() || []
      return Array.from(
        new Set(
          vizData
            .map((item) => item?.[colorDimension])
            .filter((value) => value !== undefined && value !== null)
        )
      )
    } catch (error) {
      return []
    }
  }, [currentChart, data, mapping])

  useEffect(() => {
    if (!currentChart?.visualOptions) {
      return
    }

    const optionsConfig = getOptionsConfig(currentChart.visualOptions)
    if (!optionsConfig.colorScale || visualOptions?.colorScale) {
      return
    }

    const defaultOptions = getDefaultOptionsValues(optionsConfig, mapping)
    if (!defaultOptions?.colorScale) {
      return
    }

    setVisualOptions((prev) => {
      if (prev?.colorScale) {
        return prev
      }

      return {
        ...prev,
        colorScale: defaultOptions.colorScale,
      }
    })
  }, [currentChart, mapping, setVisualOptions, visualOptions])

  useEffect(() => {
    if (!currentChart) {
      return
    }

    const optionsConfig = getOptionsConfig(currentChart.visualOptions)
    const nextThemeOptions = {}

    if (optionsConfig.background) {
      nextThemeOptions.background = brandTheme.backgroundColor
    }

    if (optionsConfig.pointColor) {
      nextThemeOptions.pointColor = brandTheme.secondaryColor
    }

    if (optionsConfig.colorScale && chartColorSyncMode === 'brand' && !chartPaletteDirty) {
      const colorDomain = getCurrentChartColorDomain()

      nextThemeOptions.colorScale = mergeExistingOpacities(
        visualOptions?.colorScale,
        buildBrandColorScaleOption(brandTheme, colorDomain)
      )
    }

    if (Object.keys(nextThemeOptions).length > 0) {
      setVisualOptions((prev) => ({
        ...prev,
        ...nextThemeOptions,
      }))
    }
  }, [
    brandTheme,
    chartColorSyncMode,
    chartPaletteDirty,
    currentChart,
    getCurrentChartColorDomain,
    setVisualOptions,
  ])

  const applyPaletteToChartColors = useCallback(
    (palette) => {
      if (!currentChart?.visualOptions) {
        return
      }

      const optionsConfig = getOptionsConfig(currentChart.visualOptions)
      if (!optionsConfig.colorScale) {
        return
      }

      const colorDomain = getCurrentChartColorDomain()
      setChartColorSyncMode('manual')
      setLastAppliedBrandPaletteSignature(null)
      setVisualOptions((prev) => {
        const nextScale = mergeExistingOpacities(
          prev?.colorScale,
          buildPaletteColorScaleOption(palette, colorDomain, brandTheme.primaryColor)
        )
        return {
          ...prev,
          colorScale: {
            ...nextScale,
            __loaded: true,
          },
        }
      })
    },
    [brandTheme.primaryColor, currentChart, getCurrentChartColorDomain, setVisualOptions]
  )

  const useThemePaletteForChartColors = useCallback(() => {
    if (!currentChart?.visualOptions) {
      return
    }

    const optionsConfig = getOptionsConfig(currentChart.visualOptions)
    const nextSignature = buildBrandPalette(brandTheme).join('|')
    setChartColorSyncMode('brand')
    setLastAppliedBrandPaletteSignature(nextSignature)

    if (!optionsConfig.colorScale) {
      return
    }

    const colorDomain = getCurrentChartColorDomain()
    setVisualOptions((prev) => {
      const nextScale = mergeExistingOpacities(
        prev?.colorScale,
        buildBrandColorScaleOption(brandTheme, colorDomain)
      )
      return {
        ...prev,
        colorScale: {
          ...nextScale,
          __loaded: true,
        },
      }
    })
  }, [brandTheme, currentChart, getCurrentChartColorDomain, setVisualOptions])

  const dismissBrandPaletteUpdatePrompt = useCallback(() => {
    setChartColorSyncMode('manual')
    setLastAppliedBrandPaletteSignature(null)
  }, [])

  const chartColorOptionMeta = useMemo(() => {
    if (!currentChart?.visualOptions) {
      return {
        hasChartColorScale: false,
        hasMappedColorField: false,
        hasConfigurableColorDomain: false,
        currentColorScaleType: null,
        colorDimensionLabel: null,
      }
    }

    const optionsConfig = getOptionsConfig(currentChart.visualOptions)
    const colorScaleOption = optionsConfig.colorScale
    if (!colorScaleOption) {
      return {
        hasChartColorScale: false,
        hasMappedColorField: false,
        hasConfigurableColorDomain: false,
        currentColorScaleType: null,
        colorDimensionLabel: null,
      }
    }

    const chartId = currentChart?.metadata?.id || ''
    const colorDimension = colorScaleOption.dimension
    const mappedColorColumn = get(mapping, `[${colorDimension}].value[0]`)
    const defaultOptions = getDefaultOptionsValues(optionsConfig)
    const hasDerivedColorDomain =
      SOURCE_DRIVEN_NODE_COLOR_CHART_IDS.has(chartId) &&
      getCurrentChartColorDomain().length > 0
    const colorDimensionLabel =
      currentChart?.dimensions?.find((dimension) => dimension.id === colorDimension)
        ?.name ||
      (SOURCE_DRIVEN_NODE_COLOR_CHART_IDS.has(chartId) ? 'Source node' : colorDimension)

    return {
      hasChartColorScale: true,
      hasMappedColorField: !!mappedColorColumn,
      hasConfigurableColorDomain: !!mappedColorColumn || hasDerivedColorDomain,
      currentColorScaleType:
        get(visualOptions, 'colorScale.scaleType') ||
        get(defaultOptions, 'colorScale.scaleType') ||
        null,
      colorDimensionLabel,
    }
  }, [currentChart, getCurrentChartColorDomain, mapping, visualOptions])

  const paletteApplyDisabledReason = useMemo(() => {
    if (!chartColorOptionMeta.hasChartColorScale) {
      return 'This chart does not have native chart color controls.'
    }
    if (!chartColorOptionMeta.hasConfigurableColorDomain) {
      return 'Map a field to Color in the Mapping step to enable chart color controls.'
    }
    if (chartColorOptionMeta.currentColorScaleType !== 'ordinal') {
      return 'Palette apply is only available in Categories mode.'
    }
    return ''
  }, [chartColorOptionMeta])

  useEffect(() => {
    if (
      chartColorSyncMode === 'defaults' &&
      get(visualOptions, 'colorScale.__loaded') &&
      chartColorOptionMeta.hasChartColorScale
    ) {
      setChartColorSyncMode('manual')
    }
  }, [chartColorOptionMeta.hasChartColorScale, chartColorSyncMode, visualOptions])

  const resetChartColorSync = useCallback(() => {
    setChartColorSyncMode('defaults')
    setLastAppliedBrandPaletteSignature(null)
  }, [])

  return {
    chartColorSyncMode,
    setChartColorSyncMode,
    lastAppliedBrandPaletteSignature,
    setLastAppliedBrandPaletteSignature,
    chartPaletteDirty,
    applyPaletteToChartColors,
    useThemePaletteForChartColors,
    dismissBrandPaletteUpdatePrompt,
    chartColorOptionMeta,
    paletteApplyDisabledReason,
    resetChartColorSync,
  }
}
